/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import { useEffect, useMemo, useState } from 'react';

/* Import configuration */
import type { TimeSeriesGroupKey } from '@/config/eeCheckConfig';

/* Import libraries */
import { DeviceSensor } from '@/lib/sensors/catalog/deviceSensor';
import {
    createDemoDeviceSensorCatalog,
    getDemoSensorTargets,
    getDemoVirtualDevices,
    type SensorGroupFilter,
} from '@/lib/sensors/demo/demoDeviceSensors';
import type {
    DemoSensorDeviceAvailability,
    DemoSensorDeviceGroupBy,
} from '@/lib/sensors/demo/demoSensorDeviceSelection';

export function useDemoSensorDeviceSelection(
    defaultCustomer: TimeSeriesGroupKey,
    sensorCatalogVersion: number,
) {
    const [selectedCustomer, setSelectedCustomer] = useState<TimeSeriesGroupKey>(defaultCustomer);
    const [selectedAvailability, setSelectedAvailability] = useState<DemoSensorDeviceAvailability>('enabled');
    const [selectedGroupFilter, setSelectedGroupFilter] = useState<SensorGroupFilter>('ALL');
    const [selectedGroupBy, setSelectedGroupBy] = useState<DemoSensorDeviceGroupBy>('system');
    const [selectedDevice, setSelectedDevice] = useState('');
    const [selectedSensorTarget, setSelectedSensorTarget] = useState('');
    const deviceSensorCatalog = useMemo(() => {
        return createDemoDeviceSensorCatalog(selectedCustomer);
    }, [selectedCustomer]);
    const virtualDevices = useMemo(() => {
        return getDemoVirtualDevices(
            deviceSensorCatalog,
            selectedGroupFilter,
            selectedAvailability === 'enabled',
        );
    }, [deviceSensorCatalog, selectedAvailability, selectedGroupFilter]);
    const deviceSensor = useMemo(() => {
        return new DeviceSensor(selectedDevice, deviceSensorCatalog);
    }, [deviceSensorCatalog, selectedDevice]);
    const sensorTargets = useMemo(() => {
        if (!selectedDevice) {
            return [];
        }

        return getDemoSensorTargets(deviceSensor, deviceSensorCatalog, selectedGroupFilter)
            .filter((target) => target.sensorName !== null);
    }, [deviceSensor, deviceSensorCatalog, selectedDevice, selectedGroupFilter]);
    const deviceMetadata = useMemo(() => {
        return deviceSensorCatalog.getSensorMetadata(selectedDevice);
    }, [deviceSensorCatalog, selectedDevice]);

    useEffect(() => {
        setSelectedCustomer(defaultCustomer);
    }, [defaultCustomer, sensorCatalogVersion]);

    useEffect(() => {
        if (!virtualDevices.length) {
            setSelectedDevice('');
            return;
        }

        if (!virtualDevices.includes(selectedDevice)) {
            setSelectedDevice(virtualDevices[0] ?? '');
        }
    }, [selectedDevice, virtualDevices]);

    useEffect(() => {
        if (!sensorTargets.length) {
            setSelectedSensorTarget('');
            return;
        }

        if (!sensorTargets.some((target) => target.sensorName === selectedSensorTarget)) {
            setSelectedSensorTarget(sensorTargets[0]?.sensorName ?? '');
        }
    }, [selectedSensorTarget, sensorTargets]);

    return {
        deviceMetadata,
        deviceSensor,
        deviceSensorCatalog,
        selectedAvailability,
        selectedCustomer,
        selectedDevice,
        selectedGroupBy,
        selectedGroupFilter,
        selectedSensorTarget,
        sensorTargets,
        setSelectedAvailability,
        setSelectedCustomer,
        setSelectedDevice,
        setSelectedGroupBy,
        setSelectedGroupFilter,
        setSelectedSensorTarget,
        virtualDevices,
    };
}
